import createRecordingTransferHandler from '@Application/Beneficiaries/Interviews/createRecordingTransferHandler';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';

/**
 * Renders nothing: registers the `'interview-recording'` transfer handler and unregisters it on
 * cleanup. The factory is called INSIDE the effect, with `[outboxPort, localInterviewPort, dispatch]`
 * as the only dependencies — calling it in the component body would produce a new function on every
 * render, re-running the effect and opening a window between unregistering the previous handler and
 * registering the next one. The port treats a `kind` with no registered handler, at the moment an
 * item is claimed, as a PERMANENT failure (`Domain/Ports/OutboxRunner/OutboxPort.ts`): a few microseconds
 * of that window is enough to lose an interview for good. `dispatch` belongs in that dependency list,
 * not behind a `React.useRef` "stabilising" it: `useDispatch()` already returns a reference stable for
 * the whole lifetime of the state manager provider, so including it costs nothing and a ref would only
 * guard against a problem that does not exist.
 *
 * This component only registers the handler; it does not decide when the transfer engine starts.
 * It must be mounted inside `PortsProvider`, immediately before `OutboxRunnerHost` — never here, and
 * never by this file — so the handler exists before the engine's own `start()` can ever claim an
 * item of this `kind` (`guides/device-ports.md`).
 */
const RecordingTransferHost: React.FC = () => {
	const outboxPort = useOutboxPort();
	const localInterviewPort = useLocalInterviewPort();
	const dispatch = useDispatch();

	React.useEffect(
		() =>
			outboxPort.registerHandler(
				'interview-recording',
				createRecordingTransferHandler(dispatch, localInterviewPort),
			),
		[outboxPort, localInterviewPort, dispatch],
	);

	return null;
};

export default RecordingTransferHost;
